import { Component, effect, inject, OnInit } from '@angular/core';
import { GlobalsStore } from '../../../stores/globals.state';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { Commune, TableConfiguration } from '../../../models/commune';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { NgIf } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { HeaderComponent } from '../../atoms/header/header.component';

@Component({
  selector: 'app-configuration-chart-table',
  standalone: true,
  imports: [
    CheckboxModule,
    DividerModule,
    DropdownModule,
    FormFieldWrapperComponent,
    FormsModule,
    NgIf,
    ReactiveFormsModule,
    InputTextModule,
    HeaderComponent,
  ],
  templateUrl: './configuration-chart-table.component.html',
  styleUrl: './configuration-chart-table.component.scss',
})
export class ConfigurationChartTableComponent implements OnInit {
  readonly communeStore = inject(CommuneStore);
  readonly globalStore = inject(GlobalsStore);
  form: FormGroup = new FormGroup({});
  initialized = false;
  items: MenuItem[] = [];
  activeCommune: Commune | null = null;
  tableConfiguration?: TableConfiguration;

  constructor(private fb: FormBuilder) {
    effect(() => {
      this.communeSetup();
    });
  }

  /**
   * Initializes the active commune setup by ensuring necessary configurations are loaded,
   * including atlas configuration and table setup. If the commune is not already active,
   * it retrieves and assigns the active commune from the global store. Additionally,
   * calls the `initForm` method if the commune is active and not yet initialized.
   *
   * @return {void} Does not return a value, performs setup operations internally.
   */
  communeSetup(): void {
    if (!this.activeCommune) {
      this.activeCommune = this.globalStore.activeCommune();
      if (!this.activeCommune) return;
      if (!this.activeCommune.atlasConfig) {
        this.activeCommune.atlasConfig = this.communeStore.baseConfig();
      }
      this.tableConfiguration = this.activeCommune.atlasConfig.table;
    }
    if (this.activeCommune && !this.initialized) {
      this.initForm();
    }
  }

  /**
   * Lifecycle hook that is called after Angular has initialized the component.
   * It is used here to perform the initial setup by invoking the `communeSetup` method.
   *
   * @return {void} No return value.
   */
  ngOnInit(): void {
    this.communeSetup();
  }

  /**
   * Initializes the form with default values based on the table configuration.
   * Configures reactive form controls and sets up a subscription to handle form value changes.
   * Marks the initialization as complete.
   *
   * @return {void} Does not return a value.
   */
  initForm(): void {
    this.form = this.fb.group({
      displayKeys: [this.tableConfiguration?.displayKeys || ''],
      captionTotal: [this.tableConfiguration?.captionTotal || ''],
      captionAverage: [this.tableConfiguration?.captionAverage || ''],
      captionDistrict: [this.tableConfiguration?.captionDistrict || ''],
      captionKey: [this.tableConfiguration?.captionKey || ''],
    });

    this.form.valueChanges.subscribe(() => {
      this.submit();
    });

    this.initialized = true;
  }

  /**
   * Updates the active commune's Atlas configuration table with the current form values
   * and persists the updated commune data to the data store.
   *
   * @return {void} Does not return a value.
   */
  submit(): void {
    if (!this.activeCommune) return;

    this.activeCommune.atlasConfig.table = {
      ...this.activeCommune.atlasConfig.table,
      ...this.form.value,
    };
    this.communeStore.update(this.activeCommune);
  }
}
